import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { useBusiness } from './BusinessContext';

const FinancialContext = createContext();

export const FinancialProvider = ({ children }) => {
  const { user } = useAuth();
  const { business } = useBusiness();
  const [transactions, setTransactions] = useState([]);
  const [balances, setBalances] = useState({
    cash: 0,
    transfer: 0,
    credit: 0,
    other: 0,
    pending: { amount: 0, count: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentClosing, setCurrentClosing] = useState(null);

  // Fetch transactions para el negocio actual
  useEffect(() => {
    if (!user?.id || !business?.id) {
      console.log('FinancialContext - Esperando usuario y negocio...');
      return;
    }
    
    console.log('FinancialContext - Cargando datos financieros para:', { 
      userId: user.id, 
      businessId: business.id 
    });
    
    fetchFinancialData();
    
    // Suscripción a cambios en financial_transactions
    const transactionsSubscription = supabase
      .channel('financial_transactions_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'financial_transactions',
          filter: `business_id=eq.${business.id}`
        }, 
        () => {
          console.log('FinancialContext - Cambio detectado en financial_transactions');
          fetchFinancialData();
        }
      )
      .subscribe();
      
    // También observar cambios en sales para actualizar montos pendientes
    const salesSubscription = supabase
      .channel('sales_payment_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'sales',
          filter: `business_id=eq.${business.id} AND payment_status=in.(paid,unpaid,partial)`
        }, 
        () => {
          console.log('FinancialContext - Cambio detectado en estado de pagos de ventas');
          fetchFinancialData();
        }
      )
      .subscribe();
    
    return () => {
      console.log('FinancialContext - Limpiando suscripciones');
      transactionsSubscription.unsubscribe();
      salesSubscription.unsubscribe();
    };
  }, [user?.id, business?.id]);

  const fetchFinancialData = async () => {
    try {
      console.log('FinancialContext - Iniciando fetchFinancialData');
      setLoading(true);
      
      // 1. Obtener el cierre actual (sin crear uno nuevo automáticamente)
      const { data: closingData, error: closingError } = await supabase
        .from('financial_closings')
        .select('*')
        .eq('business_id', business.id)
        .eq('status', 'open')
        .order('started_at', { ascending: false })
        .limit(1);
        
      if (closingError) throw closingError;
      
      if (!closingData || closingData.length === 0) {
        console.log('FinancialContext - No hay cierre activo');
        setCurrentClosing(null);
      } else {
        console.log('FinancialContext - Cierre activo encontrado:', closingData[0].id);
        setCurrentClosing(closingData[0]);
      }
      
      // 2. Obtener transacciones pendientes (solo si hay un cierre activo)
      let transactionsData = [];
      if (closingData && closingData.length > 0) {
        const { data: txData, error: transactionsError } = await supabase
          .from('financial_transactions')
          .select('*')
          .eq('business_id', business.id)
          .eq('status', 'active')
          .is('closing_id', null)
          .order('created_at', { ascending: false });
          
        if (transactionsError) throw transactionsError;
        
        console.log(`FinancialContext - ${txData?.length || 0} transacciones recuperadas`);
        transactionsData = txData || [];
        setTransactions(transactionsData);
      } else {
        // Si no hay cierre activo, no mostramos transacciones
        setTransactions([]);
      }
      
      // 3. Calcular los saldos actuales (solo para transacciones pendientes)
      let calculatedBalances = {
        cash: 0,
        transfer: 0,
        credit: 0,
        other: 0
      };
      
      // Aplicar transacciones a los saldos
      transactionsData?.forEach(transaction => {
        const amount = Number(transaction.amount);
        const method = transaction.payment_method;
        
        if (transaction.type === 'sale_payment' || transaction.type === 'income') {
          calculatedBalances[method] = (calculatedBalances[method] || 0) + amount;
        } else if (transaction.type === 'expense') {
          calculatedBalances[method] = (calculatedBalances[method] || 0) - amount;
        }
      });
      
      // 4. Obtener ventas pendientes
      const { data: pendingSales, error: pendingError } = await supabase
        .from('sales')
        .select('id, total_amount')
        .eq('business_id', business.id)
        .eq('payment_status', 'unpaid');
        
      if (pendingError) throw pendingError;
      
      const pendingAmount = pendingSales?.reduce((sum, sale) => sum + (sale.total_amount || 0), 0) || 0;
      
      setBalances({
        ...calculatedBalances,
        pending: {
          amount: pendingAmount,
          count: pendingSales?.length || 0
        }
      });
      
      console.log('FinancialContext - Datos financieros cargados', {
        currentClosing: closingData?.[0]?.id || 'ninguno',
        transactionCount: transactionsData?.length || 0,
        balances: calculatedBalances,
        pendingCount: pendingSales?.length || 0
      });
      
      setError(null);
    } catch (error) {
      console.error('FinancialContext - Error fetching financial data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Crear una nueva transacción financiera
  const createTransaction = async (transactionData) => {
    try {
      console.log('FinancialContext - Creando nueva transacción:', transactionData);
      
      const { type, amount, payment_method, description, related_entity_type, related_entity_id, metadata } = transactionData;
      
      if (!business?.id) throw new Error('No hay un negocio seleccionado');
      if (!type || !amount || !payment_method) throw new Error('Faltan datos obligatorios para la transacción');
      
      const { data, error } = await supabase
        .from('financial_transactions')
        .insert([{
          business_id: business.id,
          auth_user_id: user.id,
          amount,
          type,
          payment_method,
          description,
          related_entity_type,
          related_entity_id,
          metadata,
          created_by: user.id
        }])
        .select()
        .single();
        
      if (error) throw error;
      
      console.log('FinancialContext - Transacción creada con éxito:', data);
      
      // Actualizar estado local
      setTransactions(prev => [data, ...prev]);
      
      // Actualizar los balances
      setBalances(prev => {
        const newBalance = { ...prev };
        
        if (type === 'sale_payment' || type === 'income') {
          newBalance[payment_method] = (newBalance[payment_method] || 0) + Number(amount);
        } else if (type === 'expense') {
          newBalance[payment_method] = (newBalance[payment_method] || 0) - Number(amount);
        }
        
        return newBalance;
      });
      
      return data;
    } catch (error) {
      console.error('FinancialContext - Error creating transaction:', error);
      throw error;
    }
  };

  // Registrar pago de venta
  const registerSalePayment = async (saleId, paymentInfo) => {
    try {
      console.log('FinancialContext - Registrando pago para venta:', saleId, paymentInfo);
      
      // Crear transacciones para cada método de pago
      const transactions = [];
      
      for (const payment of paymentInfo.payments) {
        const transaction = await createTransaction({
          type: 'sale_payment',
          amount: Number(payment.amount),
          payment_method: payment.method,
          related_entity_type: 'sale',
          related_entity_id: saleId,
          description: `Pago de venta #${saleId}`,
          metadata: {
            staffId: paymentInfo.staffId,
            staffName: paymentInfo.staffName,
            payment_details: payment
          }
        });
        
        transactions.push(transaction);
      }
      
      console.log('FinancialContext - Pago registrado con éxito:', transactions);
      
      return transactions;
    } catch (error) {
      console.error('FinancialContext - Error registering sale payment:', error);
      throw error;
    }
  };

  // Cerrar caja
  const closeRegister = async (closingData) => {
    try {
      console.log('FinancialContext - Cerrando caja:', closingData);
      
      if (!currentClosing) {
        console.error('FinancialContext - Error: No hay un cierre de caja abierto');
        throw new Error('No hay un cierre de caja abierto');
      }
      
      // 1. Actualizar cierre actual
      console.log('FinancialContext - ID del cierre a actualizar:', currentClosing.id);
      
      const { data, error } = await supabase
        .from('financial_closings')
        .update({
          closing_balance: closingData.balance || {},
          closed_at: new Date().toISOString(),
          status: 'closed',
          notes: closingData.notes || null,
          discrepancy: closingData.discrepancy || {}
        })
        .eq('id', currentClosing.id)
        .select()
        .single();
        
      if (error) {
        console.error('FinancialContext - Error al actualizar cierre de caja:', error);
        throw error;
      }
      
      console.log('FinancialContext - Cierre actualizado correctamente:', data);
      
      // 2. Asociar todas las transacciones pendientes a este cierre
      try {
        // Aseguramos que el tipo sea correcto - bigint en la base de datos
        let closingId = currentClosing.id;
        // Al ser un bigint en la base de datos, no necesitamos convertir a Number
        // De hecho, podría causar problemas de precisión si es muy grande
        
        console.log('FinancialContext - Asociando transacciones al cierre ID:', closingId);
        
        // Verificar transacciones pendientes primero
        const { data: pendingTransactions, error: pendingError } = await supabase
          .from('financial_transactions')
          .select('id')
          .eq('business_id', business.id)
          .is('closing_id', null);
          
        if (pendingError) {
          console.error('FinancialContext - Error al verificar transacciones pendientes:', pendingError);
        } else {
          const pendingCount = pendingTransactions?.length || 0;
          console.log(`FinancialContext - Hay ${pendingCount} transacciones pendientes para asociar`);
          
          if (pendingCount > 0) {
            // Solo intentar actualizar si hay transacciones pendientes
            const { data: updatedData, error: updateError } = await supabase
              .from('financial_transactions')
              .update({ closing_id: closingId }) // Usar el ID tal cual viene del cierre
              .eq('business_id', business.id)
              .is('closing_id', null);
              
            if (updateError) {
              console.error('FinancialContext - Error al asociar transacciones:', updateError);
              console.error('FinancialContext - Detalle:', updateError.message);
              // No lanzamos error para no revertir el cierre
            } else {
              console.log('FinancialContext - Transacciones asociadas correctamente al cierre');
            }
          }
        }
      } catch (updateError) {
        console.error('FinancialContext - Excepción al asociar transacciones:', updateError);
        // No lanzamos el error para no revertir el cierre
      }
      
      // 3. Actualizar estado local independientemente de si hubo error en las transacciones
      setCurrentClosing(null);
      setTransactions([]);
      
      console.log('FinancialContext - Caja cerrada con éxito');
      
      return data;
    } catch (error) {
      console.error('FinancialContext - Error al cerrar la caja:', error);
      throw error;
    }
  };

  // Obtener transacciones
  const getTransactions = async (filters = {}) => {
    try {
      console.log('FinancialContext - Obteniendo transacciones con filtros:', filters);
      
      // Verificar si business existe
      if (!business) {
        console.warn('FinancialContext - No hay negocio seleccionado, devolviendo array vacío');
        return [];
      }

      let query = supabase
        .from('financial_transactions')
        .select('*')
        .eq('business_id', business.id)
        .eq('status', 'active');
      
      // Aplicar filtros adicionales
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      
      if (filters.payment_method) {
        query = query.eq('payment_method', filters.payment_method);
      }
      
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }
      
      // Ordenar por fecha
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('FinancialContext - Error fetching transactions:', error);
      throw error;
    }
  };

  // Crear un nuevo cierre (abrir caja)
  const openCashRegister = async (closingData) => {
    try {
      console.log('FinancialContext - Abriendo caja con datos:', closingData);
      
      // Verificar si ya hay un cierre abierto
      if (currentClosing) {
        console.warn('FinancialContext - Ya hay un cierre de caja abierto');
        throw new Error('Ya hay una caja abierta. Cierre la caja actual antes de abrir una nueva.');
      }
      
      // Crear el nuevo cierre
      const { data, error } = await supabase
        .from('financial_closings')
        .insert([{
          business_id: business.id,
          auth_user_id: user.id,
          opening_balance: closingData.openingBalance || {},
          notes: closingData.notes || null,
          created_by: user.id
        }])
        .select()
        .single();
        
      if (error) {
        console.error('FinancialContext - Error al crear cierre de caja:', error);
        throw error;
      }
      
      console.log('FinancialContext - Cierre creado con éxito:', data);
      
      // Actualizar estado local
      setCurrentClosing(data);
      
      // Refrescar datos financieros
      await fetchFinancialData();
      
      return data;
    } catch (error) {
      console.error('FinancialContext - Error opening cash register:', error);
      throw error;
    }
  };

  const value = {
    transactions,
    balances,
    loading,
    error,
    currentClosing,
    createTransaction,
    openCashRegister,
    registerSalePayment,
    closeRegister,
    getTransactions,
    refreshData: fetchFinancialData
  };

  return (
    <FinancialContext.Provider value={value}>
      {children}
    </FinancialContext.Provider>
  );
};

export const useFinancial = () => {
  const context = useContext(FinancialContext);
  if (!context) {
    throw new Error('useFinancial debe usarse dentro de un FinancialProvider');
  }
  return context;
};

export default FinancialContext; 